import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import React, { useState } from "react";

const getProportion = (value: number, minimum: number, maximum: number): number => {
  return (value - minimum) / (maximum - minimum);
}

export interface SpeedometerCategory {
  threshold?: number;
  color: string;
  label?: string;
}

type SpeedometerRangeProps =
  | { min: number; max: number; center?: never; delta?: never }
  | { center: number; delta: number; min?: never; max?: never }

type SpeedometerProps = SpeedometerRangeProps & {
  value: number;
  categories: SpeedometerCategory[];
  showThresholdsOnHover?: boolean;
  staticPointerColor?: string;
}

const Speedometer = (props: SpeedometerProps) => {
  const { value, categories, showThresholdsOnHover = false, staticPointerColor } = props;

  // Calculate min and max from either absolute values or center/delta
  const min = props.min ?? (props.center - props.delta);
  const max = props.max ?? (props.center + props.delta);

  const [hovering, setHovering] = useState(false);

  // Convert category thresholds to proportions for the gauge
  // Categories without a threshold use max as their threshold
  const proportionCategories = categories.map(cat => ({
    proportion: getProportion(cat.threshold ?? max, min, max),
    color: cat.color,
    threshold: cat.threshold ?? max
  }));

  // Sort by proportion to ensure correct ordering
  proportionCategories.sort((a, b) => a.proportion - b.proportion);

  const proportionValue = getProportion(value, min, max);

  const numberOfAxisSegments = 100;

  const near = (val: number, reference: number): boolean => {
    const axisSegmentProportionWidth = 1 / numberOfAxisSegments;
    const threshold = axisSegmentProportionWidth / 2;
    return ((reference - threshold) <= val) && (val <= (reference + threshold));
  };

  const [echartsInstance, setEchartsInstance] = React.useState<any>();

  React.useEffect(() => {
    echarts.connect("speedometer-group");
  }, []);

  const options = {
    series: [
      {
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        center: ['50%', '75%'],
        radius: '100%',
        min: 0,
        max: 1,
        splitNumber: 100,
        axisLine: {
          lineStyle: {
            width: 15,
            color: proportionCategories.map(cat => [cat.proportion, cat.color])
          }
        },
        pointer: {
          icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
          length: '60%',
          width: 10,
          itemStyle: {
            color: staticPointerColor ?? 'auto'
          }
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: '#464646',
          fontSize: 10,
          distance: -35,
          formatter: function (value: number) {
            if (!showThresholdsOnHover || !hovering) {
              return '';
            }

            // Show min value at start
            if (value === 0) {
              return min;
            }

            // Show threshold values
            for (const cat of proportionCategories) {
              if (near(value, cat.proportion)) {
                return cat.threshold;
              }
            }

            return '';
          }
        },
        detail: {
          show: false,
        },
        data: [
          {
            value: proportionValue
          }
        ]
      }
    ]
  };

  React.useEffect(() => {
    // trigger resize when options change to make sure the chart is always visible
    echartsInstance?.resize();
  }, [options]);

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <ReactECharts
        onChartReady={(echartsInstance) => {
          echartsInstance.group = "speedometer-group";
          setEchartsInstance(echartsInstance);
        }}
        option={options}
        notMerge={true}
        style={{ height: '100%', width: '100%' }}
      />
    </div>
  )
}

export default Speedometer;
